<template>
  <transition name="fade">
    <div v-if="show" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 60;" @keydown.esc.prevent.stop="emitClose" @keydown.tab.prevent="onTab" tabindex="-1" ref="container">
      
      <!-- Backdrop - clickable on both mobile and desktop -->
      <div v-if="!noBackdrop" style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(0,0,0,0.4); backdrop-filter: blur(4px);" @click="emitClose" aria-hidden="true"></div>
      <div v-if="noBackdrop" class="absolute inset-0 bg-black/30 md:bg-transparent md:backdrop-blur-0" @click="emitClose" aria-hidden="true"></div>
      <!-- Panel -->
  <div 
    style="position: fixed; top: 0; right: 0; bottom: 0; background-color: white; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); display: flex; flex-direction: column; z-index: 61;"
    class="w-80 max-w-[85vw] md:max-w-xs"
    role="dialog" aria-modal="true" :aria-label="title" ref="panel">
        <div class="px-4 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 class="text-sm font-semibold text-gray-800">{{ title }}</h2>
          <div class="flex items-center space-x-2">
            <span class="sr-only" aria-live="polite">{{ activeCount }} active filters</span>
            <slot name="badge">
              <span v-if="activeCount > 0" class="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">{{ activeCount }}</span>
            </slot>
            <button type="button" class="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500" @click="emitClose" aria-label="Close filters" ref="closeBtn">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-600" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
            </button>
          </div>
        </div>
        <div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <slot />
        </div>
        <div v-if="!hideApplyButton" class="px-4 py-4 border-t border-gray-200 flex items-center justify-between">
          <button type="button" class="text-xs font-medium text-red-700 hover:text-red-800 hover:underline" @click="emitClear" :disabled="applying">Clear All</button>
          <div class="flex items-center space-x-2">
            <button type="button" class="px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-50" @click="emitClose" :disabled="applying">Cancel</button>
            <button type="button" class="px-3 py-1.5 text-xs font-medium rounded-md bg-navy text-white hover:bg-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-navy-light disabled:opacity-50" @click="emitApply" :disabled="applying">Apply</button>
          </div>
        </div>
        <div v-else class="px-4 py-2 border-t border-gray-200 flex items-center justify-center">
          <button type="button" class="text-xs font-medium text-red-700 hover:text-red-800 hover:underline" @click="emitClear">Clear All</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'FilterDrawer',
  props: {
    show: { type: Boolean, default: false },
    title: { type: String, default: 'Filters' },
    activeCount: { type: Number, default: 0 },
    applying: { type: Boolean, default: false },
    noBackdrop: { type: Boolean, default: false },
    hideApplyButton: { type: Boolean, default: false }
  },
  emits: ['close','apply','clear'],
  watch: {
    show(val){
      if(val){
        // Focus the first focusable after next tick
        this.$nextTick(()=>{
          this.cacheFocusable();
          if(this.firstFocusable) {
            this.firstFocusable.focus();
          }
        });
      }
    }
  },
  methods: {
    emitClose(){ this.$emit('close'); },
    emitApply(){ this.$emit('apply'); },
    emitClear(){ this.$emit('clear'); },
    cacheFocusable(){
      const focusableSelectors = 'a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      const nodes = this.$refs.panel ? Array.from(this.$refs.panel.querySelectorAll(focusableSelectors)) : [];
      this.focusable = nodes.filter(n=>!n.hasAttribute('disabled'));
      this.firstFocusable = this.focusable[0];
      this.lastFocusable = this.focusable[this.focusable.length -1];
    },
    onTab(e){
      if(!this.show) return;
      if(!this.focusable || this.focusable.length === 0) return;
      const active = document.activeElement;
      if(e.shiftKey){
        if(active === this.firstFocusable){
          e.preventDefault();
          this.lastFocusable.focus();
        }
      } else {
        if(active === this.lastFocusable){
          e.preventDefault();
          this.firstFocusable.focus();
        }
      }
    }
  }
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
/* Slide animation for panel */
.fade-enter-active .drawer-panel, .fade-leave-active .drawer-panel { transition: transform .25s ease; }
.fade-enter-from .drawer-panel { transform: translateX(100%); }
.fade-enter-to .drawer-panel { transform: translateX(0); }
.fade-leave-from .drawer-panel { transform: translateX(0); }
.fade-leave-to .drawer-panel { transform: translateX(100%); }
</style>
